// Damage calculation pipeline (9 layers + shield) and application.
//
// References:
// - docs/05-combat-sim §5.7.1 (calculation flow)
// - docs/05-combat-sim §5.7.7 (default numbers)
// - docs/05-combat-sim §5.11.1 (shield ordering)
//
// Two functions:
// 1. calculateDamage(): pure, deterministic given (attacker, target, input, rng).
//    Returns the final scalar damage and whether the hit critted.
// 2. applyDamage(): mutates target's shields (consume in-order) and hp.
//    Splits the result into absorbedByShield / absorbedByHp.
import { affinityMultiplier } from '@renavora/shared';
/** Armor mitigation softness constant. See docs §5.7.7 (K = 50 default). */
const ARMOR_K = 50;
/** Hard caps borrowed from the docs. */
const ARMOR_MITIGATION_CAP = 0.8;
const RESIST_MAX = 0.75;
const RESIST_MIN = -0.5;
/** ±5% multiplicative variance per docs §5.7.1 (Layer H). */
const VARIANCE_LOW = 0.95;
const VARIANCE_RANGE = 0.1;
/** Hit chance baseline before accuracy / evasion adjustments. */
const HIT_CHANCE_BASE = 0.95;
const HIT_CHANCE_FLOOR = 0.05;
const HIT_CHANCE_CEIL = 1.0;
/** Damage reduction from armor (Layer F). Physical damage only. */
export function armorMitigation(armor, defenderLevel) {
    if (armor <= 0)
        return 0;
    const denom = armor + ARMOR_K * Math.max(1, defenderLevel);
    return Math.min(ARMOR_MITIGATION_CAP, armor / denom);
}
/** Hit chance per docs §5.7.7. clamp(0.95 + atkAcc - defEva, 0.05, 1.0). */
export function hitChance(attackerAccuracy, defenderEvasion) {
    const raw = HIT_CHANCE_BASE + attackerAccuracy - defenderEvasion;
    return Math.min(HIT_CHANCE_CEIL, Math.max(HIT_CHANCE_FLOOR, raw));
}
/**
 * Pure damage calculation. Consumes RNG for the hit / crit / variance rolls
 * but does not touch the entities.
 */
export function calculateDamage(attacker, target, input, rng) {
    // Step 0: hit check.
    const shieldMiss = Math.max(0, input.missChanceBonus ?? 0);
    const hc = Math.max(HIT_CHANCE_FLOOR, hitChance(attacker.stats.accuracy, target.stats.evasion) - shieldMiss);
    const hitRoll = rng.nextFloat();
    if (hitRoll >= hc) {
        return { hit: false, finalDamage: 0, crit: false };
    }
    const baseDmg = attacker.stats.weaponBase * input.power;
    // Layer A: aggregate atkBonus%.
    const layerA = baseDmg * (1 + attacker.stats.atkBonus);
    // Layer B: per-element bonus%.
    const elementBonus = attacker.stats.elementBonus[input.element] ?? 0;
    const layerB = layerA * (1 + elementBonus);
    // Layer C: 7-element affinity.
    const layerC = layerB * affinityMultiplier(input.element, target.element);
    // Layer D: crit roll.
    let isCrit;
    if (input.ignoreCrit) {
        isCrit = false;
    }
    else if (input.forceCrit) {
        isCrit = true;
    }
    else {
        isCrit = rng.nextFloat() < attacker.stats.critRate;
    }
    const critMult = isCrit ? 1 + attacker.stats.critDamage : 1;
    const layerD = layerC * critMult;
    // Layer E: situational multiplier (race specialisation, backstab, opening flurry).
    const layerE = layerD * (input.situational ?? 1);
    // Layer F: physical-only armor mitigation.
    const armorMit = input.element === 'physical' ? armorMitigation(target.stats.armor, target.level) : 0;
    const layerF = layerE * (1 - armorMit);
    // Layer G: elemental-only resistance.
    let effResist = 0;
    if (input.element !== 'physical') {
        const r = target.stats.resistance[input.element] ?? 0;
        effResist = Math.min(RESIST_MAX, Math.max(RESIST_MIN, r));
    }
    const layerG = layerF * (1 - effResist);
    // Layer H: ±5% multiplicative variance.
    const variance = VARIANCE_LOW + rng.nextFloat() * VARIANCE_RANGE;
    const finalDamage = Math.max(0, layerG * variance);
    return { hit: true, finalDamage, crit: isCrit };
}
/** Mutate target shields and HP. Shields are consumed in array order. */
export function applyDamage(target, damage) {
    let remaining = Math.max(0, damage);
    let absorbedByShield = 0;
    for (const layer of target.shields) {
        if (remaining <= 0)
            break;
        const taken = Math.min(layer.amount, remaining);
        layer.amount -= taken;
        absorbedByShield += taken;
        remaining -= taken;
    }
    // Drop fully-consumed shields, preserving order.
    if (absorbedByShield > 0) {
        let writeIdx = 0;
        for (let readIdx = 0; readIdx < target.shields.length; readIdx++) {
            const layer = target.shields[readIdx];
            if (layer.amount > 0) {
                target.shields[writeIdx++] = layer;
            }
        }
        target.shields.length = writeIdx;
    }
    const absorbedByHp = Math.min(remaining, target.hp);
    target.hp -= absorbedByHp;
    return {
        absorbedByShield,
        absorbedByHp,
        killed: target.hp <= 0,
    };
}
//# sourceMappingURL=damage.js.map